import { useMutation, useQuery } from '@apollo/react-hooks';
import { CreateOrderValidationSchema, getMinimalOrderDate } from '@dnb/common';
import { Grid, useMediaQuery } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { Button } from 'components';
import { Form, Formik } from 'formik';
import { BaseOrderForm } from 'forms';
import {
  CreateOrderMutationData,
  CreateOrderMutationInput,
  CREATE_ORDER_MUTATION,
  GetPickupLocationsQueryData,
  GetPickupLocationsQueryVariables,
  GET_PICKUP_LOCATIONS_QUERY,
} from 'gql';
import { PickupLocation } from 'gql/types';
import { mapValues } from 'lodash';
import React, { useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { breakpoints } from 'styles';
import { AuthContext } from 'context';

interface Props {}

interface OrderForm {
  pickupLocation: string;
  amount: number;
  comments: string;
  pickupDate: Date;
}

const formModel: OrderForm = {
  pickupLocation: '',
  amount: 0,
  comments: '',
  pickupDate: getMinimalOrderDate(),
};

const PopulatedOrderForm = (_props: Props) => {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  const isDesktop = useMediaQuery(breakpoints.md, {
    defaultMatches: true,
  });

  const { data } = useQuery<
    GetPickupLocationsQueryData,
    GetPickupLocationsQueryVariables
  >(GET_PICKUP_LOCATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
  });

  const [createOrder, { loading: isCreating }] = useMutation<
    CreateOrderMutationData,
    CreateOrderMutationInput
  >(CREATE_ORDER_MUTATION, {
    onCompleted: () => {
      toast.success(t('notification:order:success'));
      history.push('/myorders');
    },
    onError: () => {
      toast.error(t('notification:order:error'));
    },
  });

  const onSubmit = ({
    amount,
    pickupLocation,
    comments,
    pickupDate,
  }: OrderForm) => {
    createOrder({
      variables: {
        createOrderInput: { amount, pickupLocation, comments, pickupDate },
      },
    });
  };

  const generate = (location: PickupLocation) => {
    const { city, zipCode, streetName, buildingNumber, comments } = mapValues(
      location,
      val => val || ''
    );

    return `${zipCode} ${city}${streetName &&
      `, ul. ${streetName}${buildingNumber &&
        ` ${buildingNumber}`}`} ${comments && `(${comments})`}`;
  };

  const getJustify = (index: number) => {
    if (!isDesktop) {
      return 'flex-start';
    }

    return index % 2 === 0 ? 'flex-start' : 'flex-end';
  };

  const getLabelPlacement = (index: number) => {
    if (!isDesktop) {
      return 'end';
    }
    return index % 2 === 0 ? 'end' : 'start';
  };

  const getPrices = (amount: number) => {
    const price = user?.price || 10;
    const calculated = parseFloat((amount * price).toFixed(2));

    return (
      <div>
        <div>{t('labels:priceFor', { price: user?.price })}</div>
        <div>{t('labels:priceTotal', { price: calculated })}</div>
      </div>
    );
  };

  if (!data?.getPickupLocationList) {
    return <div>{t('errors:pickupLocations')}</div>;
  }

  return (
    <Formik
      initialValues={{
        ...formModel,
        pickupLocation: data.getPickupLocationList[0]?._id || '',
      }}
      onSubmit={onSubmit}
      innerRef={formRef as any}
      enableReinitialize
      validationSchema={CreateOrderValidationSchema(t)}
    >
      {props => {
        return (
          <Form>
            <Grid container justify="center" spacing={2}>
              <Grid item xs={12} md={8} lg={6} spacing={2} container>
                <Grid item xs={12}>
                  <BaseOrderForm />
                </Grid>
                <Grid item xs={12}>
                  {getPrices(props.values.amount)}
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    isLoading={isCreating}
                  >
                    {t('buttons:order')}
                  </Button>
                </Grid>
              </Grid>
              <Grid container justify="space-around">
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    style={{ padding: '16px 16px' }}
                  >
                    {t('labels:pickupLocation')}
                  </FormLabel>
                  <RadioGroup
                    name="pickupLocation"
                    value={props.values.pickupLocation}
                    onChange={props.handleChange}
                  >
                    <Grid container spacing={1}>
                      {data.getPickupLocationList.map((location, index) => (
                        <Grid
                          item
                          container
                          xs={12}
                          md={6}
                          key={location._id}
                          justify={getJustify(index)}
                          style={{
                            borderBottom: '1px solid lightgray',
                            padding: '16px 16px',
                          }}
                        >
                          <FormControlLabel
                            value={location._id}
                            control={<Radio />}
                            label={generate(location)}
                            labelPlacement={getLabelPlacement(index)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PopulatedOrderForm;
